import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import FeatherIcon from "feather-icons-react";
import domtoimage from "dom-to-image";
import aiIcon from "src/assets/icons/ai-icon.svg";
import "./AiSideChat.scss";
import { useAiGatewayChat, useReportAiGatewayConversation } from "src/graphql/aiGateway";
import { renderBitacoraCommentWithMentions } from "src/components/flujocaja/bitacora/renderMarkdown";
import AiGatewayCharts, { buildChartsFromDetails } from "./AiGatewayCharts";
import {
  closeDocumentDetail,
  isPascaleDocumentModalSession,
  openPascaleDocumentDetail,
  stripDocumentDetailFromUrl,
} from "src/utils/documentModal";

const WELCOME_MESSAGE = [
  "Hola, soy **Pascale**, tu asistente financiero.\n\n",
  "Pregúntame en lenguaje natural — no necesitas comandos exactos:\n\n",
  "- **¿Cuánto vendí en marzo?** → total con gráfico\n",
  "- **Ventas mes a mes de 2025** → evolución mensual\n",
  "- **Top 5 proveedores del año** → ranking con montos\n",
  "- **Desglosa las ventas de febrero** → tabla de facturas\n",
  "- **Compras vs ventas de marzo** → comparación lado a lado\n",
  "- **¿Qué facturas tengo por cobrar?** → cartera pendiente\n\n",
  "Puedes usar \u201ceste mes\u201d, \u201cmes pasado\u201d, un rango (ej: _enero a junio 2025_) o un año completo. Pulsa \u2139 para más tips.",
].join("");

const INFO_TIPS = [
  { icon: "calendar", text: "Períodos flexibles: \"marzo 2026\", \"ventas 2025\", \"desde enero a junio 2024\" o simplemente \"este mes\"" },
  { icon: "bar-chart-2", text: "Rankings: \"top 10 clientes de 2025\", \"¿a qué proveedor le compré más?\" o \"¿en qué mes vendí más?\"" },
  { icon: "trending-up", text: "Evolución: \"ventas mes a mes\" o \"compras desde enero 2024 a marzo 2026\". El gráfico aparece al lado en pantalla completa" },
  { icon: "file-text", text: "Detalle de facturas: \"desglosa las ventas de febrero\" o \"muéstrame las facturas por pagar\" abre la tabla de documentos" },
  { icon: "git-branch", text: "Seguimiento natural: pregunta un total, luego \"¿y las compras?\" o \"Desglosar documentos\" " },
];

const SPLIT_CHAT_PCT_DEFAULT = 44;
const SPLIT_CHAT_PCT_MIN = 28;
const SPLIT_CHAT_PCT_MAX = 68;

const pascaleContextStorageKey = (companyId) =>
  `pascale_ctx_${companyId ? String(companyId) : "no_company"}`;

const AiSideChat = ({ open, onClose, companyId }) => {
  const history = useHistory();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [splitChatPct, setSplitChatPct] = useState(SPLIT_CHAT_PCT_DEFAULT);
  const splitRowRef = useRef(null);
  const splitDragRef = useRef(null);
  const [vizPanelVisible, setVizPanelVisible] = useState(false);
  const [chartAnimReady, setChartAnimReady] = useState(false);
  const [chartFullscreen, setChartFullscreen] = useState(false);
  const [fsViewport, setFsViewport] = useState({ w: 0, h: 0 });
  const [vizSidebarCollapsed, setVizSidebarCollapsed] = useState(false);
  const [vizPageIndex, setVizPageIndex] = useState(0);
  const [vizViewMode, setVizViewMode] = useState("chart");
  const vizStackLenRef = useRef(0);
  const [exportBusy, setExportBusy] = useState(false);
  const vizExportRef = useRef(null);
  const fsChartExportRef = useRef(null);
  const shellRef = useRef(null);
  const infoRef = useRef(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: WELCOME_MESSAGE },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportStatus, setReportStatus] = useState(null);
  const messagesContainerRef = useRef(null);
  const conversationStateRef = useRef(null);
  const { executeMutation: aiGatewayChat } = useAiGatewayChat();
  const { executeMutation: reportConversation, fetching: reportSubmitting } = useReportAiGatewayConversation();

  const vizStack = useMemo(() => {
    const out = [];
    messages.forEach((m, messageIndex) => {
      if (m.role !== "assistant" || !m.details) return;
      const charts = buildChartsFromDetails(m.details);
      if (!charts.length) return;
      const title = charts[0]?.title ? String(charts[0].title).slice(0, 48) : "";
      out.push({
        messageIndex,
        details: m.details,
        label: title || `Visualización ${out.length + 1}`,
      });
    });
    return out;
  }, [messages]);

  const vizCount = vizStack.length;
  const safeVizPage = Math.min(vizPageIndex, Math.max(0, vizCount - 1));
  const activeVizDetails = vizCount ? vizStack[safeVizPage]?.details : null;

  const vizAnimKey = useMemo(() => {
    try {
      return JSON.stringify(activeVizDetails ?? {});
    } catch {
      return String(activeVizDetails);
    }
  }, [activeVizDetails]);

  const activeVizChartSpecs = useMemo(() => buildChartsFromDetails(activeVizDetails), [activeVizDetails]);
  const vizIsTableOnly =
    activeVizChartSpecs.length > 0 && activeVizChartSpecs.every((c) => c && c.type === "table");
  const effectiveVizViewMode = vizIsTableOnly ? "table" : vizViewMode;

  useEffect(() => {
    if (vizIsTableOnly) setVizViewMode("table");
  }, [vizIsTableOnly, vizAnimKey]);

  const showSplitViz = Boolean(isExpanded && vizCount > 0);

  const fsChartHeight = useMemo(() => {
    const h = fsViewport.h || (typeof window !== "undefined" ? window.innerHeight : 800);
    return Math.min(680, Math.max(280, Math.floor(h * 0.62)));
  }, [fsViewport.h]);

  useEffect(() => {
    if (!open) return;
    if (!companyId) {
      conversationStateRef.current = null;
      return;
    }
    try {
      const raw = sessionStorage.getItem(pascaleContextStorageKey(companyId));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          conversationStateRef.current = parsed;
        }
      }
    } catch (_) {
      /* ignore corrupt storage */
    }
  }, [open, companyId]);

  useEffect(() => {
    if (!open) return;
    // Memoria de follow-up: sessionStorage por empresa + ref en memoria mientras el modal está abierto.
    // Al abrir el modal, mantenemos el estado in-memory actual.
    setIsExpanded(false);
    setOverlayVisible(false);
    setShowInfo(false);
    setChartFullscreen(false);
    setSplitChatPct(SPLIT_CHAT_PCT_DEFAULT);
    setVizSidebarCollapsed(false);
    setVizPageIndex(0);
    setVizViewMode("chart");
    vizStackLenRef.current = 0;
  }, [open]);

  useEffect(() => {
    if (vizCount === 0) {
      setVizPageIndex(0);
      vizStackLenRef.current = 0;
      return;
    }
    if (vizCount > vizStackLenRef.current) {
      setVizPageIndex(vizCount - 1);
    } else {
      setVizPageIndex((p) => Math.min(p, vizCount - 1));
    }
    vizStackLenRef.current = vizCount;
  }, [vizCount]);

  // UX: si se generó un gráfico, abrir panel derecho y priorizar vista "Gráficos".
  // Esto evita que el usuario "no vea" el gráfico porque estaba en vista tabla o el panel estaba cerrado.
  useEffect(() => {
    if (!open) return;
    if (vizCount <= 0) return;
    const newestDetails = vizStack[vizCount - 1]?.details;
    const newestCharts = buildChartsFromDetails(newestDetails);
    const hasNonTableChart = Array.isArray(newestCharts) && newestCharts.some((c) => c && c.type && c.type !== "table");
    if (!hasNonTableChart) return;

    // Expandir y mostrar el panel derecho automáticamente.
    setIsExpanded(true);
    setOverlayVisible(true);
    setVizSidebarCollapsed(false);
    setVizViewMode("chart");
  }, [open, vizCount, vizStack]);

  useEffect(() => {
    if (!showSplitViz) {
      setVizPanelVisible(false);
      setChartAnimReady(false);
      return;
    }
    setVizPanelVisible(false);
    setChartAnimReady(false);
    const showPanel = window.setTimeout(() => setVizPanelVisible(true), 0);
    const t = window.setTimeout(() => setChartAnimReady(true), 700);
    return () => {
      window.clearTimeout(showPanel);
      window.clearTimeout(t);
    };
  }, [showSplitViz, vizAnimKey]);

  useEffect(() => {
    if (!chartFullscreen) return;
    const onResize = () => setFsViewport({ w: window.innerWidth, h: window.innerHeight });
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [chartFullscreen]);

  useEffect(() => {
    if (!showInfo) return;
    const onClick = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setShowInfo(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [showInfo]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (chartFullscreen) {
        setChartFullscreen(false);
        return;
      }
      if (isExpanded) setIsExpanded(false);
      else onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, isExpanded, onClose, chartFullscreen]);

  // Bloquear scroll del background cuando el modal está expandido.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!open) return;
    if (!isExpanded) return;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    const prevPaddingRight = body.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    body.style.overflow = "hidden";
    if (scrollBarWidth > 0) body.style.paddingRight = `${scrollBarWidth}px`;
    return () => {
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPaddingRight;
    };
  }, [open, isExpanded]);

  useEffect(() => {
    if (!showSplitViz) {
      setChartFullscreen(false);
      setVizSidebarCollapsed(false);
    }
  }, [showSplitViz]);

  const clearDocumentModalUrlForExport = useCallback(() => {
    stripDocumentDetailFromUrl(history, location);
  }, [history, location]);

  const resetConversation = useCallback(() => {
    conversationStateRef.current = null;
    if (companyId) {
      try {
        sessionStorage.removeItem(pascaleContextStorageKey(companyId));
      } catch (_) {
        /* ignore */
      }
    }
    setMessages([{ role: "assistant", content: WELCOME_MESSAGE }]);
    setInputValue("");
    setLoading(false);
    setReportOpen(false);
    setReportStatus(null);
    setVizPageIndex(0);
    setVizViewMode("chart");
    setChartFullscreen(false);
    setVizSidebarCollapsed(false);
    vizStackLenRef.current = 0;
    clearDocumentModalUrlForExport();
  }, [companyId, clearDocumentModalUrlForExport]);

  const handlePascaleDocumentLink = useCallback(
    (mongoId, linkCfg) => {
      openPascaleDocumentDetail(mongoId, history, location, {
        modulePath: linkCfg?.modulePath || "/pago_proveedores",
        extraSearch: linkCfg?.extraSearch || { inProcessPage: "1", processedPage: "1" },
      });
    },
    [history, location],
  );

  useEffect(() => {
    if (open || !isPascaleDocumentModalSession()) return;
    closeDocumentDetail(history, location);
  }, [open, history, location]);

  const exportChartFromEl = async (el) => {
    if (!el) return;
    clearDocumentModalUrlForExport();
    setExportBusy(true);
    try {
      const dataUrl = await domtoimage.toPng(el, {
        quality: 1,
        bgcolor: "#ffffff",
      });
      const link = document.createElement("a");
      link.download = `pascale-visualizacion-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error("export chart", err);
    } finally {
      setExportBusy(false);
    }
  };

  const handleExportPanelChart = async () => {
    if (exportBusy) return;
    if (effectiveVizViewMode === "chart" && !chartAnimReady) return;
    const wasCollapsed = vizSidebarCollapsed;
    if (wasCollapsed) setVizSidebarCollapsed(false);
    await new Promise((r) => window.setTimeout(r, wasCollapsed ? 450 : 0));
    await exportChartFromEl(vizExportRef.current);
    if (wasCollapsed) setVizSidebarCollapsed(true);
  };

  const handleExportFullscreenChart = async () => {
    if (exportBusy) return;
    await exportChartFromEl(fsChartExportRef.current);
  };

  const downloadTextFile = (filename, text, mime) => {
    try {
      const blob = new Blob([text], { type: mime || "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("download file", err);
    }
  };

  const exportActiveTableAsCsv = () => {
    if (exportBusy) return;
    clearDocumentModalUrlForExport();
    const table = activeVizChartSpecs.find((c) => c && c.type === "table");
    if (!table) return;
    const title = String(table.title || "tabla").trim() || "tabla";
    const safeTitle = title
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);
    const labelHeader = table.labelHeader || "Etiqueta";
    const series = Array.isArray(table.series) ? table.series : [];
    const data = Array.isArray(table.data) ? table.data : [];
    const hasMom = data.some((r) => r && r.variationPctMoM != null);
    const headers = [
      labelHeader,
      ...series.map((s) => String(s?.name || s?.dataKey || "")),
      ...(hasMom ? ["Var. MoM %"] : []),
    ];
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      if (/[\",\\n]/.test(s)) return `\"${s.replace(/\"/g, '\"\"')}\"`;
      return s;
    };
    const lines = [];
    lines.push(headers.map(esc).join(","));
    data.forEach((row) => {
      const cols = [];
      cols.push(row?.name != null ? row.name : "");
      series.forEach((s) => cols.push(row?.[s.dataKey] != null ? row[s.dataKey] : ""));
      if (hasMom) cols.push(row?.variationPctMoM != null ? Number(row.variationPctMoM) : "");
      lines.push(cols.map(esc).join(","));
    });
    const csv = "\uFEFF" + lines.join("\n"); // BOM para Excel
    downloadTextFile(`pascale-${safeTitle || "tabla"}.csv`, csv, "text/csv;charset=utf-8");
  };

  const onSplitResizeStart = (e) => {
    e.preventDefault();
    const row = splitRowRef.current;
    if (!row) return;
    const rect = row.getBoundingClientRect();
    const startX = e.clientX;
    const startPct = splitChatPct;
    splitDragRef.current = { startX, startPct, rowWidth: rect.width };

    const onMove = (ev) => {
      const drag = splitDragRef.current;
      if (!drag) return;
      const dx = ev.clientX - drag.startX;
      const deltaPct = (dx / drag.rowWidth) * 100;
      const next = Math.min(SPLIT_CHAT_PCT_MAX, Math.max(SPLIT_CHAT_PCT_MIN, startPct + deltaPct));
      setSplitChatPct(next);
    };
    const onUp = () => {
      splitDragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, open, showSplitViz, vizSidebarCollapsed]);

  if (!open) return null;

  const handleSend = async () => {
    const text = (inputValue || "").trim();
    if (!text || loading) return;

    const nextMessages = [...messages, { role: "user", content: text }];
    setMessages(nextMessages);
    setInputValue("");
    setLoading(true);

    try {
      const { data, error } = await aiGatewayChat({
        messages: nextMessages.map((m) => ({
          role: m.role,
          content: m.content,
        })),
        companyId: companyId ? String(companyId) : null,
        conversationState: conversationStateRef.current || undefined,
      });
      if (error) throw error;

      const payload = data?.aiGatewayChat;
      const reply = payload?.reply;
      const details = payload?.details ?? null;
      const trace = payload?.trace ?? null;
      const ui = payload?.ui ?? null;
      const contextForNextTurn = payload?.contextForNextTurn ?? null;
      if (payload?.trace || payload?.details) {
        console.info("[AiSideChat][technical]", {
          trace: payload?.trace || null,
          details: payload?.details || null,
        });
      }
      if (contextForNextTurn && typeof contextForNextTurn === "object") {
        conversationStateRef.current = contextForNextTurn;
        if (companyId) {
          try {
            sessionStorage.setItem(
              pascaleContextStorageKey(companyId),
              JSON.stringify(contextForNextTurn),
            );
          } catch (_) {
            /* quota or private mode */
          }
        }
      }
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            String(reply || "").trim() ||
            "No pude procesar tu solicitud en este momento. Por favor, inténtalo de nuevo más tarde.",
          details,
          trace,
          ui,
        },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "No pude procesar tu solicitud en este momento. Por favor, inténtalo de nuevo más tarde.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSubmitReport = async () => {
    if (reportSubmitting) return;
    setReportStatus(null);
    try {
      const fullSession = messages.map((m) => ({
        role: m?.role === "assistant" ? "assistant" : "user",
        content: String(m?.content || ""),
        trace: m?.trace || null,
        details: m?.details || null,
        ui: m?.ui || null,
      }));

      const payload = {
        companyId: companyId ? String(companyId) : null,
        reason: String(reportReason || "").trim() || null,
        messages: fullSession.map((m) => ({ role: m.role, content: m.content })),
        metadata: {
          source: "AiSideChat",
          fullSession,
          conversationState: conversationStateRef.current || null,
          reportedAt: new Date().toISOString(),
        },
      };

      const { data, error } = await reportConversation(payload);
      if (error) throw error;

      const result = data?.reportAiGatewayConversation;
      if (result?.success) {
        setReportStatus({ type: "ok", text: result?.message || "Reporte enviado correctamente." });
        setReportReason("");
        setTimeout(() => {
          setReportOpen(false);
          setReportStatus(null);
        }, 1200);
        return;
      }
      setReportStatus({ type: "error", text: "No se pudo enviar el reporte." });
    } catch (err) {
      setReportStatus({ type: "error", text: "No se pudo enviar el reporte. Intenta nuevamente." });
      console.error("[AiSideChat] report conversation error", err);
    }
  };

  const toggleExpanded = () => {
    setIsExpanded((v) => {
      const next = !v;
      if (next) setOverlayVisible(true);
      return next;
    });
    if (isExpanded) setOverlayVisible(true);
  };

  const closeExpanded = () => {
    if (isExpanded) setIsExpanded(false);
    else onClose?.();
  };

  const handleShellTransitionEnd = (e) => {
    if (e.target !== shellRef.current) return;
    if (!isExpanded && overlayVisible) setOverlayVisible(false);
  };

  const renderMessageList = () => (
    <>
      {messages.map((m, idx) => (
        <div key={idx} className={`ai-sidechat__msg ai-sidechat__msg--${m.role}`}>
          {m.role === "assistant" && (
            <div className="ai-sidechat__avatar">
              <img src={aiIcon} alt="" aria-hidden />
            </div>
          )}
          <div className="ai-sidechat__bubble">
            {m.role === "user" ? (
              String(m.content || "")
                .split("\n")
                .map((line, i) => <p key={i}>{line}</p>)
            ) : (
              <div
                className="ai-sidechat__markdown"
                dangerouslySetInnerHTML={{
                  __html: renderBitacoraCommentWithMentions(String(m.content || "")),
                }}
              />
            )}
          </div>
        </div>
      ))}
      {loading && (
        <div className="ai-sidechat__msg ai-sidechat__msg--assistant">
          <div className="ai-sidechat__avatar">
            <img src={aiIcon} alt="" aria-hidden />
          </div>
          <div className="ai-sidechat__typing">
            <span />
            <span />
            <span />
          </div>
        </div>
      )}
    </>
  );

  const panel = (
    <div
      className={`ai-sidechat-layer ${overlayVisible ? "ai-sidechat-layer--expanded" : ""} ${
        showSplitViz ? "ai-sidechat-layer--split-viz" : ""
      }`}
    >
      <button
        type="button"
        className="ai-sidechat-backdrop"
        aria-label="Cerrar chat"
        onClick={closeExpanded}
        tabIndex={overlayVisible ? 0 : -1}
      />

      <div
        className={`ai-sidechat-shell ${isExpanded ? "ai-sidechat-shell--expanded" : ""}`}
        role="dialog"
        aria-modal="true"
        ref={shellRef}
        onTransitionEnd={handleShellTransitionEnd}
      >
        <div
          className={`ai-sidechat ${isExpanded ? "ai-sidechat--expanded" : ""} ${showSplitViz ? "ai-sidechat--expanded-split" : ""}`}
        >
          <div className="ai-sidechat__header">
            <div className="ai-sidechat__title">
              <span>PASCALE</span>
            </div>
            <div className="ai-sidechat__headerActions">
              <button
                type="button"
                className="ai-sidechat__resetBtn"
                title="Reiniciar: se perderá la conversación hasta ahora y comenzarás una nueva."
                aria-label="Reiniciar conversación"
                onClick={resetConversation}
                disabled={loading}
              >
                <FeatherIcon icon="rotate-ccw" size={16} strokeWidth={2.1} />
              </button>
              <div className="ai-sidechat__infoWrap" ref={infoRef}>
                <button
                  type="button"
                  className={`ai-sidechat__infoBtn ${showInfo ? "ai-sidechat__infoBtn--active" : ""}`}
                  onClick={() => setShowInfo((v) => !v)}
                  title="Tips para conversar con Pascale"
                >
                  <FeatherIcon icon="info" size={15} strokeWidth={2.2} />
                </button>
                {showInfo && (
                  <div className="ai-sidechat__infoPopover">
                    <p className="ai-sidechat__infoPopover__title">Tips para conversar con Pascale</p>
                    <ul className="ai-sidechat__infoPopover__list">
                      {INFO_TIPS.map((tip, i) => (
                        <li key={i}>
                          <FeatherIcon icon={tip.icon} size={13} strokeWidth={2} />
                          <span>{tip.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="ai-sidechat__headerReportBtn"
                title="Reportar conversación"
                onClick={() => {
                  setReportOpen(true);
                  setReportStatus(null);
                }}
              >
                <FeatherIcon icon="flag" size={15} strokeWidth={2.1} />
                <span>Reportar</span>
              </button>
              {showSplitViz && vizSidebarCollapsed && (
                <button
                  type="button"
                  className="ai-sidechat__headerVizBtn"
                  title="Mostrar visualización"
                  onClick={() => setVizSidebarCollapsed(false)}
                >
                  <FeatherIcon icon="activity" size={16} strokeWidth={2.2} />
                  {vizCount > 1 ? <span className="ai-sidechat__headerVizBadge">{vizCount}</span> : null}
                </button>
              )}
              <button
                type="button"
                onClick={toggleExpanded}
                className="ai-sidechat__iconBtn"
                title={isExpanded ? "Salir de pantalla completa" : "Abrir en pantalla completa"}
              >
                <FeatherIcon icon={isExpanded ? "minimize-2" : "maximize-2"} size={16} strokeWidth={2} />
              </button>
              <button type="button" onClick={closeExpanded} className="ai-sidechat__iconBtn" title="Cerrar">
                <FeatherIcon icon="x" size={18} strokeWidth={2} />
              </button>
            </div>
          </div>

          {showSplitViz ? (
            <div className="ai-sidechat__splitRow" ref={splitRowRef}>
              <div
                className="ai-sidechat__splitChat"
                style={{ flex: vizSidebarCollapsed ? "1 1 auto" : `0 0 ${splitChatPct}%` }}
              >
                <div ref={messagesContainerRef} className="ai-sidechat__messages">
                  {renderMessageList()}
                </div>
                <div className="ai-sidechat__inputWrap">
                  <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Escribe tu pregunta…"
                    rows={2}
                    className="ai-sidechat__input"
                    disabled={false}
                  />
                  <button
                    type="button"
                    className="ai-sidechat__send"
                    onClick={handleSend}
                    disabled={!inputValue.trim() || loading}
                    title="Enviar"
                  >
                    <FeatherIcon icon="send" size={18} strokeWidth={2} />
                  </button>
                </div>
              </div>
              {!vizSidebarCollapsed && (
                <div
                  className="ai-sidechat__splitResizeHandle"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Ajustar ancho del panel"
                  onMouseDown={onSplitResizeStart}
                />
              )}
              <aside
                className={`ai-sidechat__splitViz ${vizPanelVisible ? "ai-sidechat__splitViz--visible" : ""} ${
                  vizSidebarCollapsed ? "ai-sidechat__splitViz--collapsed" : ""
                }`}
                aria-label="Visualización de datos"
                aria-hidden={vizSidebarCollapsed}
              >
                <div className="ai-sidechat__vizPanel" ref={vizExportRef}>
                  <div className="ai-sidechat__vizPanelHead">
                    <div className="ai-sidechat__vizPanelIcon">
                      <FeatherIcon icon="activity" size={18} strokeWidth={2.2} />
                    </div>
                    <div className="ai-sidechat__vizPanelHeadText">
                      <span className="ai-sidechat__vizPanelTitle">Visualización</span>
                      <span className="ai-sidechat__vizPanelSub">
                        {vizCount > 1
                          ? `${vizStack[safeVizPage]?.label || "Serie"} · ${safeVizPage + 1}/${vizCount}`
                          : "Progreso y series de tu consulta"}
                      </span>
                    </div>
                    <div className="ai-sidechat__vizPanelHeadActions">
                      {!vizIsTableOnly ? (
                        <div className="ai-sidechat__vizModeToggle" role="group" aria-label="Tipo de vista">
                          <button
                            type="button"
                            className={`ai-sidechat__vizModeBtn ${vizViewMode === "chart" ? "ai-sidechat__vizModeBtn--active" : ""}`}
                            title="Gráficos"
                            onClick={() => setVizViewMode("chart")}
                          >
                            <FeatherIcon icon="bar-chart-2" size={15} strokeWidth={2} />
                          </button>
                          <button
                            type="button"
                            className={`ai-sidechat__vizModeBtn ${vizViewMode === "table" ? "ai-sidechat__vizModeBtn--active" : ""}`}
                            title="Tablas"
                            onClick={() => setVizViewMode("table")}
                          >
                            <FeatherIcon icon="grid" size={15} strokeWidth={2} />
                          </button>
                        </div>
                      ) : null}
                      <button
                        type="button"
                        className="ai-sidechat__vizHeadIconBtn"
                        title="Ocultar panel (las visualizaciones se mantienen)"
                        onClick={() => setVizSidebarCollapsed(true)}
                      >
                        <FeatherIcon icon="eye-off" size={16} strokeWidth={2} />
                      </button>
                      <button
                        type="button"
                        className="ai-sidechat__vizHeadIconBtn"
                        title={
                          effectiveVizViewMode === "table" || chartAnimReady
                            ? "Exportar como imagen PNG"
                            : "Espera a que cargue el gráfico"
                        }
                        onClick={handleExportPanelChart}
                        disabled={(effectiveVizViewMode === "chart" && !chartAnimReady) || exportBusy}
                      >
                        <FeatherIcon icon="download" size={16} strokeWidth={2} />
                      </button>
                      {effectiveVizViewMode === "table" ? (
                        <button
                          type="button"
                          className="ai-sidechat__vizHeadIconBtn"
                          title="Exportar tabla como CSV (Excel)"
                          onClick={exportActiveTableAsCsv}
                          disabled={exportBusy || !vizCount}
                        >
                          <FeatherIcon icon="file-text" size={16} strokeWidth={2} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="ai-sidechat__vizExpandBtn"
                        title="Ampliar"
                        onClick={() => setChartFullscreen(true)}
                      >
                        <FeatherIcon icon="maximize-2" size={16} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                  {vizCount > 1 ? (
                    <div className="ai-sidechat__vizPager" role="navigation" aria-label="Cambiar visualización">
                      <button
                        type="button"
                        className="ai-sidechat__vizPagerBtn"
                        disabled={safeVizPage <= 0}
                        onClick={() => setVizPageIndex((p) => Math.max(0, p - 1))}
                        title="Anterior"
                      >
                        <FeatherIcon icon="chevron-left" size={18} strokeWidth={2} />
                      </button>
                      <div className="ai-sidechat__vizPagerDots">
                        {vizStack.map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            className={`ai-sidechat__vizPagerDot ${i === safeVizPage ? "ai-sidechat__vizPagerDot--active" : ""}`}
                            title={vizStack[i]?.label || `Vista ${i + 1}`}
                            onClick={() => setVizPageIndex(i)}
                            aria-current={i === safeVizPage ? "true" : undefined}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="ai-sidechat__vizPagerBtn"
                        disabled={safeVizPage >= vizCount - 1}
                        onClick={() => setVizPageIndex((p) => Math.min(vizCount - 1, p + 1))}
                        title="Siguiente"
                      >
                        <FeatherIcon icon="chevron-right" size={18} strokeWidth={2} />
                      </button>
                    </div>
                  ) : null}
                  <div className="ai-sidechat__vizPanelBody">
                    {!chartAnimReady && effectiveVizViewMode === "chart" ? (
                      <div className="ai-sidechat__chartPlaceholder" aria-hidden>
                        <div className="ai-sidechat__vizLoading">
                          <div className="ai-sidechat__vizLoadingAvatar">
                            <img src={aiIcon} alt="" aria-hidden />
                          </div>
                          <div className="ai-sidechat__vizLoadingDots">
                            <span className="ai-sidechat__vizLoadingDot" />
                            <span className="ai-sidechat__vizLoadingDot" />
                            <span className="ai-sidechat__vizLoadingDot" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <AiGatewayCharts
                        details={activeVizDetails}
                        panelMode
                        chartAnimationReady={effectiveVizViewMode === "chart"}
                        animationKey={vizAnimKey}
                        viewMode={effectiveVizViewMode}
                        enableDocumentLinks
                        onDocumentLinkClick={handlePascaleDocumentLink}
                      />
                    )}
                  </div>
                </div>
              </aside>
            </div>
          ) : (
            <>
              <div ref={messagesContainerRef} className="ai-sidechat__messages">
                {renderMessageList()}
              </div>
              <div className="ai-sidechat__inputWrap">
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Escribe tu pregunta…"
                  rows={2}
                  className="ai-sidechat__input"
                  disabled={false}
                />
                <button
                  type="button"
                  className="ai-sidechat__send"
                  onClick={handleSend}
                  disabled={!inputValue.trim() || loading}
                  title="Enviar"
                >
                  <FeatherIcon icon="send" size={18} strokeWidth={2} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const chartFsOverlay =
    chartFullscreen && activeVizDetails ? (
      <div className="ai-sidechat-chartfs" role="presentation">
        <button
          type="button"
          className="ai-sidechat-chartfs__backdrop"
          aria-label="Cerrar vista ampliada"
          onClick={() => setChartFullscreen(false)}
        />
        <div className="ai-sidechat-chartfs__dialog" role="dialog" aria-modal="true" aria-label="Gráfico ampliado">
          <div className="ai-sidechat-chartfs__head">
            <div className="ai-sidechat-chartfs__headLeft">
              <span className="ai-sidechat-chartfs__title">Visualización</span>
              {vizCount > 1 ? (
                <span className="ai-sidechat-chartfs__subtitle">
                  {safeVizPage + 1} / {vizCount}
                </span>
              ) : null}
            </div>
            <div className="ai-sidechat-chartfs__headActions">
              {vizCount > 1 ? (
                <div className="ai-sidechat-chartfs__pagerMini">
                  <button
                    type="button"
                    className="ai-sidechat-chartfs__pagerMiniBtn"
                    disabled={safeVizPage <= 0}
                    onClick={() => setVizPageIndex((p) => Math.max(0, p - 1))}
                    aria-label="Visualización anterior"
                  >
                    <FeatherIcon icon="chevron-left" size={18} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className="ai-sidechat-chartfs__pagerMiniBtn"
                    disabled={safeVizPage >= vizCount - 1}
                    onClick={() => setVizPageIndex((p) => Math.min(vizCount - 1, p + 1))}
                    aria-label="Visualización siguiente"
                  >
                    <FeatherIcon icon="chevron-right" size={18} strokeWidth={2} />
                  </button>
                </div>
              ) : null}
              {!vizIsTableOnly ? (
                <div className="ai-sidechat-chartfs__modeToggle" role="group" aria-label="Vista">
                  <button
                    type="button"
                    className={`ai-sidechat-chartfs__modeBtn ${vizViewMode === "chart" ? "ai-sidechat-chartfs__modeBtn--on" : ""}`}
                    title="Gráfico"
                    onClick={() => setVizViewMode("chart")}
                  >
                    <FeatherIcon icon="bar-chart-2" size={16} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className={`ai-sidechat-chartfs__modeBtn ${vizViewMode === "table" ? "ai-sidechat-chartfs__modeBtn--on" : ""}`}
                    title="Tabla"
                    onClick={() => setVizViewMode("table")}
                  >
                    <FeatherIcon icon="grid" size={16} strokeWidth={2} />
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                className="ai-sidechat-chartfs__iconBtn"
                title="Exportar como PNG"
                onClick={handleExportFullscreenChart}
                disabled={exportBusy}
              >
                <FeatherIcon icon="download" size={18} strokeWidth={2} />
              </button>
              <button
                type="button"
                className="ai-sidechat-chartfs__close"
                onClick={() => setChartFullscreen(false)}
                title="Cerrar"
              >
                <FeatherIcon icon="x" size={20} strokeWidth={2} />
              </button>
            </div>
          </div>
          <div className="ai-sidechat-chartfs__body" ref={fsChartExportRef}>
            <AiGatewayCharts
              details={activeVizDetails}
              panelMode
              chartAnimationReady={effectiveVizViewMode === "chart"}
              animationKey={`${vizAnimKey}-fs`}
              chartHeight={fsChartHeight}
              viewMode={effectiveVizViewMode}
              enableDocumentLinks
              onDocumentLinkClick={handlePascaleDocumentLink}
            />
          </div>
        </div>
      </div>
    ) : null;

  const reportOverlay = reportOpen ? (
    <div className="ai-sidechat-report" role="presentation">
      <button
        type="button"
        className="ai-sidechat-report__backdrop"
        aria-label="Cerrar reporte"
        onClick={() => {
          if (!reportSubmitting) setReportOpen(false);
        }}
      />
      <div className="ai-sidechat-report__dialog" role="dialog" aria-modal="true" aria-label="Reportar conversación">
        <div className="ai-sidechat-report__head">
          <span>Reportar conversación</span>
          <button
            type="button"
            className="ai-sidechat-report__close"
            onClick={() => {
              if (!reportSubmitting) setReportOpen(false);
            }}
            disabled={reportSubmitting}
          >
            <FeatherIcon icon="x" size={18} strokeWidth={2} />
          </button>
        </div>
        <div className="ai-sidechat-report__body">
          <p>
            Se enviará la conversación completa de esta sesión para revisión.
          </p>
          <textarea
            className="ai-sidechat-report__textarea"
            placeholder="Motivo del reporte (opcional)..."
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            maxLength={2000}
            rows={4}
            disabled={reportSubmitting}
          />
          <div className="ai-sidechat-report__meta">Mensajes en sesión: {messages.length}</div>
          {reportStatus ? (
            <div className={`ai-sidechat-report__status ai-sidechat-report__status--${reportStatus.type}`}>
              {reportStatus.text}
            </div>
          ) : null}
        </div>
        <div className="ai-sidechat-report__actions">
          <button
            type="button"
            className="ai-sidechat-report__btn ai-sidechat-report__btn--ghost"
            onClick={() => setReportOpen(false)}
            disabled={reportSubmitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="ai-sidechat-report__btn ai-sidechat-report__btn--primary"
            onClick={handleSubmitReport}
            disabled={reportSubmitting}
          >
            {reportSubmitting ? "Enviando..." : "Enviar reporte"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {createPortal(panel, document.body)}
      {chartFsOverlay ? createPortal(chartFsOverlay, document.body) : null}
      {reportOverlay ? createPortal(reportOverlay, document.body) : null}
    </>
  );
};

export default AiSideChat;
